import React, { BaseSyntheticEvent, FC, useState } from 'react'

import { useMutation } from '@apollo/client'
import { FormProvider, useController, useForm } from 'react-hook-form'

import { EMAIL_PATTERN } from 'Const'
import { SignUpStates } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import useMounted from 'Hooks/useMounted'
import Send from 'Icons/send.svg'
import X from 'Icons/x.svg'
import { SIGN_UP } from 'Pages/Chat/const/gql/signUp'
import FormErrors from 'Pages/Checkout/Form/FormErrors'
import { signUpCallback } from 'Utils/signUpCallback'

import ErrorMessage from 'Components/ErrorMessage'
import PhoneInput from 'Components/PhoneInput'

import styles from './index.module.scss'

interface FormValues {
  email: string
  firstName: string
  lastName: string
  phone?: string
}

const DEFAULT_FORM_VALUES: FormValues = {
  email: '',
  firstName: '',
  lastName: '',
}

interface Props {
  hideSignupForm(): void
}

const SignUpForm: FC<Props> = ({ hideSignupForm }) => {
  const [state, setState] = useState<SignUpStates | undefined>()
  const { locationCountryIsoCodes } = window.VARIABLES
  const defaultCountry = locationCountryIsoCodes[0] || 'US'
  const isMounted = useMounted()

  const form = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const { control, register, handleSubmit } = form
  const { field: phoneField } = useController({
    name: 'phone',
    control,
    rules: {
      required: {
        value: state === SignUpStates.MULTIPLE,
        message: 'Phone number is required',
      },
    },
  })

  const [signUp] = useMutation(SIGN_UP)

  const onSubmit = useEvent((e?: BaseSyntheticEvent) => {
    const submit = handleSubmit((input: FormValues) => {
      void signUp({ variables: { input } }).then(({ data }) => {
        if (!isMounted()) return
        if (data.signUpCustomer.state === SignUpStates.COMPLETED || data.signUpCustomer.state === SignUpStates.FORM) {
          hideSignupForm()
        }

        setState(data.signUpCustomer.state as SignUpStates)
        signUpCallback()
      })
    }, console.error)

    void submit(e)
  })

  return (
    <li className="chatBox__dialog--message msgFrom">
      <X className={`sociavore-icon ${styles.closeIcon}`} onClick={hideSignupForm} />

      <div className="chatBox__dialog--message-body">
        <div className="chat-sign-up">
          <FormProvider {...form}>
            <form className="chat-sign-up__form" onSubmit={onSubmit}>
              <div className="chat-sign-up__caption">
                <p className="chat-sign-up__paragraph">
                  Thanks for messaging us. We try to be as responsive as possible. We&apos;ll get back to you soon.
                </p>

                <p className="chat-sign-up__paragraph mb-3">
                  In case we don&apos;t reply to you quick enough, give us another way to reach you.
                </p>
              </div>

              <input
                ref={register({ required: 'First name is required' })}
                aria-label="First Name"
                className="form-control"
                name="firstName"
                placeholder="Your first name"
              />
              <ErrorMessage as={FormErrors} name="firstName" />
              <input
                ref={register({ required: 'Last name is required' })}
                aria-label="Last Name"
                className="form-control"
                name="lastName"
                placeholder="Your last name"
              />
              <ErrorMessage as={FormErrors} name="lastName" />
              <input
                ref={register({
                  required: 'Email is required',
                  pattern: { value: EMAIL_PATTERN, message: 'Email incorrect' },
                })}
                aria-label="Email"
                className="form-control mb-3"
                name="email"
                placeholder="Your e-mail"
                type="email"
              />
              <ErrorMessage as={FormErrors} name="email" />
              {state === SignUpStates.MULTIPLE ? (
                <>
                  <PhoneInput
                    {...phoneField}
                    required
                    addInternationalOption={false}
                    autoComplete="tel"
                    countryOptionsOrder={locationCountryIsoCodes}
                    defaultCountry={defaultCountry}
                    id="customer-phone"
                    numberInputProps={{ className: 'form-control' }}
                    placeholder="Enter your phone number"
                  />
                  <ErrorMessage as={FormErrors} name="phone" />
                </>
              ) : null}
              <button aria-label="Send" className="btn btn-accent" type="submit">
                <Send />
                <span>Send</span>
              </button>
            </form>
          </FormProvider>
        </div>
      </div>
    </li>
  )
}

export default SignUpForm
